import { GrowthBook } from '@growthbook/growthbook';
import { Config } from 'core/common/contexts';
import { JSONValue } from 'core/common/entities';
import { normalizeError } from 'core/common/errors';
import { AppEnvironment } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { getIsDevMode } from 'core/common/utils/getIsDevMode';
import {
  ExperimentDescription,
  ExperimentRawResult,
  FeatureDefinition,
  FeatureFlagsAttributes,
  FeatureFlagsConfig,
  FeatureRawResult,
  FeaturesPayload,
  FeatureValue,
  LoadFeaturesOptions,
} from '../entities';
import { FeatureFlags } from './FeatureFlags';

export class GrowthBookFeatureFlags implements FeatureFlags {
  private growthBookInstance!: GrowthBook;

  private appEnvironment: AppEnvironment;

  private appConfig: Config;

  private logger: Logger;

  private passedConfig: FeatureFlagsConfig;

  private trackExperimentListeners: Array<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (experiment: ExperimentDescription<any>, result: ExperimentRawResult<any>) => void
  > = [];

  private attributesUpdateListeners: Array<(attributes: FeatureFlagsAttributes) => void> = [];

  constructor(
    passedConfig: FeatureFlagsConfig,
    appEnvironment: AppEnvironment,
    appConfig: Config,
    logger: Logger,
    apiHostRequestHeaders?: Record<string, string>,
  ) {
    const notifyTrackExperimentCallback = this.notifyTrackExperimentListeners.bind(this);

    this.appConfig = appConfig;
    this.logger = logger;
    this.appEnvironment = appEnvironment;
    this.passedConfig = passedConfig ?? {};

    const config = {
      ...passedConfig,
      apiHost: this.appConfig.growthbook.apiHost,
      clientKey: this.appConfig.growthbook.clientKey,
      decryptionKey: this.appConfig.growthbook.decryptionKey,
      apiHostRequestHeaders,
      // Skip all experiments
      qaMode: this.appEnvironment.isTestingEnv(),
      enableDevMode: !this.appConfig.isProduction || getIsDevMode(),
    };

    this.growthBookInstance = new GrowthBook({
      ...config,
      trackingCallback: notifyTrackExperimentCallback,
    });
  }

  async loadFeatures(
    options: LoadFeaturesOptions = {},
  ): Promise<{ [x: string]: FeatureDefinition<JSONValue> } | undefined> {
    try {
      await this.growthBookInstance.init(options);

      const base = this.passedConfig.features ?? {};
      const fetched = this.getFeatures() ?? {};
      const features = { ...base, ...fetched };

      this.growthBookInstance.setFeatures(features);

      return features;
    } catch (e) {
      const error = normalizeError(e);
      this.logger.error(error);
    }
  }

  getFeatures(): FeatureFlagsConfig['features'] {
    return this.growthBookInstance.getFeatures() as FeatureFlagsConfig['features'];
  }

  getPayload(): FeaturesPayload {
    return this.growthBookInstance.getPayload();
  }

  destroy() {
    this.growthBookInstance.destroy();
  }

  getSplitId(): string {
    const attributes = this.growthBookInstance.getAttributes();

    const splitId = attributes.id;

    return splitId;
  }

  setForcedFeatureValues(featureValues: Record<string, unknown>) {
    const map = new Map(Object.entries(featureValues));
    this.growthBookInstance.setForcedFeatures(map);
  }

  getAttributes(): FeatureFlagsAttributes {
    return this.growthBookInstance.getAttributes();
  }

  async setAttributes(attributes: FeatureFlagsAttributes): Promise<void> {
    const currentAttributes = this.getAttributes();
    const newAttributes = {
      ...currentAttributes,
      ...attributes,
    };
    await this.growthBookInstance.setAttributes(newAttributes);
    this.notifyAttributesUpdateListeners(newAttributes);
  }

  setIdentifier(identifier: string) {
    this.setAttributes({
      id: identifier,
    });
  }

  setStateUpdateHandler(callback: () => void): () => void {
    return this.growthBookInstance.subscribe(callback);
  }

  isFeatureOn(featureName: string) {
    return this.growthBookInstance.isOn(featureName);
  }

  isFeatureOff(featureName: string) {
    return this.growthBookInstance.isOff(featureName);
  }

  getFeatureValue<T extends FeatureValue = FeatureValue>(featureName: string, defaultValue?: T): T {
    return this.growthBookInstance.getFeatureValue(featureName, defaultValue) as T;
  }

  getFeatureRawResult<T extends FeatureValue>(featureName: string): FeatureRawResult<T | null> {
    return this.growthBookInstance.evalFeature(featureName);
  }

  getExperimentGroup<T extends FeatureValue = string>(experimentName: string): T | null {
    const result = this.getFeatureRawResult<T>(experimentName);

    if (!result || !result.value) {
      this.logger.warn('Experiment not found', {
        data: experimentName,
      });
      return null;
    }

    return result.value;
  }

  onTrackExperiment<T extends FeatureValue = FeatureValue>(
    callback: (experiment: ExperimentDescription<T>, result: ExperimentRawResult<T>) => void,
  ) {
    this.trackExperimentListeners.push(callback);
  }

  onAttributesUpdated(callback: (attributes: FeatureFlagsAttributes) => void) {
    this.attributesUpdateListeners.push(callback);
  }

  private notifyTrackExperimentListeners(
    experiment: ExperimentDescription,
    result: ExperimentRawResult,
  ) {
    const features = this.getFeatures();

    const preventTrack =
      !!features && features[experiment.key] && features[experiment.key].shouldNotTrack;

    if (preventTrack) return;

    this.trackExperimentListeners.forEach((listener) => {
      try {
        listener(experiment, result);
      } catch (err) {
        const error = normalizeError(err);
        this.logger.error(error);
      }
    });
  }

  private notifyAttributesUpdateListeners(attributes: FeatureFlagsAttributes) {
    this.attributesUpdateListeners.forEach((listener) => {
      try {
        listener(attributes);
      } catch (err) {
        const error = normalizeError(err);
        this.logger.error(error);
      }
    });
  }
}
